import { useQuery } from "@tanstack/react-query";

import { disabledCustomerTenderUserSchema } from "~/shared/schemas/auto-dispatch-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

import { tenderUserTypeIds } from "../utils/constants.ts";

export const customerDisabledTenderUsersQueryKey = (customerId: number) =>
	["customerDisabledAutoDispatchTenderUsers", customerId] as const;

const getDisabledCustomerTenderUsers = async (
	customerId: number,
	signal?: AbortSignal,
) => {
	const signalOptions = signal ? { signal } : undefined;
	const resp = await getAndValidate(
		apiRoutes.GETCUSTOMERDISABLEDTENDERUSERS.replace(
			":customerId",
			customerId.toString(),
		),
		disabledCustomerTenderUserSchema.array(),
		signalOptions,
	);

	if (resp.success) {
		return resp.data;
	} else {
		throw new Error(
			`Failed to fetch disabled customer tender users: ${resp.error}`,
		);
	}
};

export const useGetCustomerTenderSettings = (customerId: number) => {
	const {
		data: disabledTenderUsers,
		error: customerTenderSettingsError,
		isLoading: customerTenderSettingsIsLoading,
		isFetching: customerTenderSettingsIsFetching,
		refetch: refetchCustomerTenderSettings,
	} = useQuery({
		queryKey: customerDisabledTenderUsersQueryKey(customerId),
		queryFn: async ({ signal }: { signal: AbortSignal }) =>
			getDisabledCustomerTenderUsers(customerId, signal),
		enabled: customerId > 0,
		staleTime: 0,
		gcTime: 0,
	});

	const disabledTenderUserTypeIds = new Set(
		disabledTenderUsers?.map((user) => user.tenderUserTypeID) ?? [],
	);
	const customerTenderSettings =
		customerId > 0 && disabledTenderUsers
			? {
					customerId,
					canDispatchOnCustomerTender: !disabledTenderUserTypeIds.has(
						tenderUserTypeIds.customer,
					),
					canDispatchOnTQLTender: !disabledTenderUserTypeIds.has(
						tenderUserTypeIds.internal,
					),
					canDispatchOnVendorTender: !disabledTenderUserTypeIds.has(
						tenderUserTypeIds.vendor,
					),
				}
			: undefined;

	return {
		customerTenderSettings,
		disabledTenderUsers,
		customerTenderSettingsError,
		customerTenderSettingsIsLoading,
		customerTenderSettingsIsFetching,
		refetchCustomerTenderSettings,
	};
};
